import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, ArrowRight } from 'lucide-react';
import axios from 'axios';

const ForgotPasswordPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!email.trim()) { setError('יש להזין כתובת אימייל'); return; }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) { setError('כתובת אימייל לא תקינה'); return; }
    setLoading(true);
    try {
      await axios.post('/api/auth/forgot-password', { email });
      setSent(true);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'אירעה שגיאה, נסה שוב');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      dir="rtl"
      className="min-h-screen relative flex items-center justify-center p-4 overflow-hidden bg-gradient-to-br from-[#1565C0] via-[#311B92] to-[#6200EA]"
    >
      <div className="absolute top-[-15%] right-[-10%] w-[40vw] h-[40vw] max-w-[480px] max-h-[480px] rounded-full bg-white/5 blur-3xl pointer-events-none" />
      <div className="absolute bottom-[-20%] left-[-10%] w-[50vw] h-[50vw] max-w-[600px] max-h-[600px] rounded-full bg-violet-400/10 blur-3xl pointer-events-none" />

      <div className="relative z-10 w-full max-w-md bg-white dark:bg-[#1E1E1E] rounded-3xl shadow-md-4 overflow-hidden">
        <div className="h-1.5 w-full bg-gradient-to-l from-[#6200EA] to-[#1565C0]" />
        <div className="p-5 md:p-8">
          {/* Logo */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-[#1565C0] to-[#6200EA] shadow-md-3 mb-4">
              <span className="text-2xl font-extrabold text-white">A+</span>
            </div>
            <h1 className="text-3xl font-extrabold bg-gradient-to-l from-[#6200EA] to-[#1565C0] bg-clip-text text-transparent">
              Amutech
            </h1>
            <p className="text-sm text-[#49454F] dark:text-[#CAC4D0] mt-1">
              מערכת ניהול עמותות
            </p>
          </div>

          <div className="text-center mb-6">
            <h2 className="text-xl font-bold text-[#1C1B1F] dark:text-[#E6E1E5]">שכחתי סיסמא</h2>
            <p className="text-sm text-[#49454F] dark:text-[#CAC4D0] mt-1">
              הזן את כתובת המייל שלך ונשלח לך קישור לאיפוס הסיסמא
            </p>
          </div>

          {sent ? (
            <div className="text-center space-y-4">
              <div className="flex items-start gap-2 bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-900/50 text-green-700 dark:text-green-400 px-4 py-3 rounded-xl text-sm">
                <span className="mt-0.5">✅</span>
                <span>אם כתובת המייל קיימת במערכת, ישלח אליה קישור לאיפוס הסיסמא.</span>
              </div>
              <Link
                to="/login"
                className="inline-flex items-center gap-2 text-[#1565C0] hover:underline text-sm font-medium"
              >
                <ArrowRight size={16} />
                חזרה לכניסה
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="relative">
                <label className="block text-xs font-semibold text-[#49454F] dark:text-[#CAC4D0] mb-1.5 mr-1">
                  כתובת אימייל
                </label>
                <div className="relative">
                  <Mail size={17} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#BDBDBD] pointer-events-none" />
                  <input
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="md-input"
                    style={{ direction: 'ltr', textAlign: 'left', paddingLeft: '2.75rem', paddingRight: '1rem' }}
                    required
                  />
                </div>
              </div>

              {error && (
                <div className="flex items-start gap-2 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900/50 text-[#B00020] dark:text-[#CF6679] px-4 py-3 rounded-xl text-sm">
                  <span className="mt-0.5">⚠️</span>
                  <span>{error}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full flex items-center justify-center gap-2 py-3.5 px-6 rounded-full bg-gradient-to-l from-[#6200EA] to-[#1565C0] text-white font-semibold text-base shadow-md-2 hover:shadow-md-3 hover:opacity-95 active:opacity-90 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed mt-2"
              >
                {loading ? (
                  <div className="w-5 h-5 rounded-full border-2 border-white border-t-transparent animate-spin" />
                ) : (
                  'שלח לינק איפוס'
                )}
              </button>

              <div className="text-center mt-4">
                <Link
                  to="/login"
                  className="inline-flex items-center gap-1 text-[#1565C0] hover:underline text-sm font-medium"
                >
                  <ArrowRight size={14} />
                  חזרה לכניסה
                </Link>
              </div>
            </form>
          )}

          <p className="text-center text-xs text-[#BDBDBD] dark:text-[#49454F] mt-6">
            מערכת ניהול מאובטחת · Amutech © 2026
          </p>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
